"use client"

import { FirebaseError } from "firebase/app"
import {
  GoogleAuthProvider,
  UserCredential,
  signInWithCredential,
} from "firebase/auth"
import { getFirebaseAuth } from "@/lib/auth/getFirebaseAuth"

// Vercel preview deployment URLs (e.g. <project>-<hash>.vercel.app) can't be
// registered as authorized OAuth redirect domains in Firebase, so direct
// sign-in via signInWithPopup fails with auth/unauthorized-domain. To work
// around this, preview builds route Google sign-in through a stable proxy
// origin (siro-stg-oauth.web.app) that IS authorized. The proxy completes the
// OAuth flow and posts the resulting tokens back via postMessage.
//
// NEXT_PUBLIC_VERCEL_ENV is set automatically by Vercel for all deployments.
export const isPreviewInstance =
  process.env.NEXT_PUBLIC_VERCEL_ENV === "preview"

const OAUTH_PROXY_ORIGIN = "https://siro-stg-oauth.web.app"

type OAuthProxyMessage = {
  provider: "google"
  idToken: string
  accessToken: string
}

const isOAuthProxyMessage = (data: unknown): data is OAuthProxyMessage => {
  if (typeof data !== "object" || data === null) return false
  const obj = data as Record<string, unknown>
  return (
    obj.provider === "google" &&
    typeof obj.idToken === "string" &&
    obj.idToken.length > 0 &&
    typeof obj.accessToken === "string" &&
    obj.accessToken.length > 0
  )
}

const signInWithOAuthProxy = (path: string): Promise<OAuthProxyMessage> =>
  new Promise((resolve, reject) => {
    // Pass our origin explicitly — popup windows often omit the Referer
    // header, and the proxy requires it for origin validation.
    const url = new URL(`${OAUTH_PROXY_ORIGIN}${path}`)
    url.searchParams.set("origin", window.location.origin)
    url.searchParams.set("mode", "popup")
    const popup = window.open(
      url.toString(),
      "siro-oauth-proxy",
      "width=500,height=600",
    )
    if (!popup) {
      reject(
        new FirebaseError(
          "auth/popup-blocked",
          "Please enable popups for this site to sign in",
        ),
      )
      return
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== OAUTH_PROXY_ORIGIN) return
      cleanup()
      if (event.data?.error) {
        reject(new Error(event.data.error))
      } else if (!isOAuthProxyMessage(event.data)) {
        reject(
          new FirebaseError(
            "auth/internal-error",
            "Received invalid OAuth proxy message",
          ),
        )
      } else {
        resolve(event.data)
      }
    }

    const cleanup = () => {
      window.removeEventListener("message", onMessage)
      clearInterval(pollInterval)
      if (!popup.closed) popup.close()
    }

    // Poll for popup closure instead of relying on focus events, which can
    // race with an incoming OAuth success message and cause it to be lost.
    const pollInterval = setInterval(() => {
      if (popup.closed) {
        cleanup()
        reject(
          new FirebaseError(
            "auth/cancelled-popup-request",
            "Sign-in cancelled",
          ),
        )
      }
    }, 500)

    window.addEventListener("message", onMessage)
  })

export const signInGoogleViaProxy = async (): Promise<UserCredential> => {
  const msg = await signInWithOAuthProxy("/oauth/authorize")
  const credential = GoogleAuthProvider.credential(msg.idToken, msg.accessToken)
  return signInWithCredential(getFirebaseAuth(), credential)
}
